import { baseApi } from "@/shared/api/baseApi";
import { Meta, SortType } from "@/shared/types";
import { UserClient } from "../model/types";

export interface UsersResponse {
  data: UserClient[];
  meta?: Meta;
}

export interface UsersParams {
  page?: number;
  per_page?: number;
  sortCreatedAt?: SortType;
  searchName?: string;
  searchEmail?: string;
  searchCourseId?: number;
}

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<UsersResponse, UsersParams | void>({
      query: (params) => {
        const queryParams: Record<string, string | number> = {
          page: params?.page || 1,
          "per-page": params?.per_page || 10,
        };

        if (
          params?.sortCreatedAt !== undefined &&
          params?.sortCreatedAt !== null
        ) {
          queryParams["withSorts[created_at]"] = params.sortCreatedAt;
        }

        if (params?.searchName?.trim()) {
          queryParams.searchName = params.searchName.trim();
        }
        if (params?.searchEmail?.trim()) {
          queryParams.searchEmail = params.searchEmail.trim();
        }
        if (params?.searchCourseId != null && params.searchCourseId > 0) {
          queryParams.searchCourseId = params.searchCourseId;
        }

        return {
          url: "/api/admin/users?with[]=courses",
          params: queryParams,
        };
      },
      providesTags: ["Users-list"],
    }),

    getUser: builder.query<UserClient, { userId: number | string }>({
      query: ({ userId }) => ({
        url: `/api/admin/users/${userId}?with[]=courses`,
        method: "GET",
      }),
      providesTags: (_result, _error, { userId }) => [
        { type: "User-list", id: String(userId) },
      ],
    }),

    deleteUser: builder.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/api/admin/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users-list"],
    }),

    createUser: builder.mutation<UserClient, FormData>({
      query: (data) => ({
        url: "/api/admin/users",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Users-list"],
    }),

    attachUserToCourse: builder.mutation<
      void,
      { userId: number | string; data: FormData }
    >({
      query: ({ userId, data }) => ({
        url: `/api/admin/users/${userId}/attach-course`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        "Users-list",
        { type: "User-list", id: String(userId) },
      ],
    }),

    detachUserFromCourse: builder.mutation<
      void,
      { userId: number | string; courseId: number }
    >({
      query: ({ userId, courseId }) => ({
        url: `/api/admin/users/${userId}/detach-course`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ course_id: courseId }),
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        "Users-list",
        { type: "User-list", id: String(userId) },
      ],
    }),

    updateUser: builder.mutation<
      UserClient,
      { userId: number | string; data: FormData }
    >({
      query: ({ userId, data }) => ({
        url: `/api/admin/users/${userId}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        "Users-list",
        { type: "User-list", id: String(userId) },
      ],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useDeleteUserMutation,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useAttachUserToCourseMutation,
  useDetachUserFromCourseMutation,
} = userApi;
